import React, { createContext, useState } from 'react'

import { api } from 'utils/api'
import { getError } from 'utils/helpers/getError'
import { useCustomContext } from 'utils/helpers/useCustomContext'
import { GenericResponse, GenericStateType } from 'utils/types'

import {
  ShareVehicleAddContactType,
  ShareVehicleConfirmPinType,
  ShareVehicleContactType,
  ShareVehicleContextType,
  ShareVehicleGetInvitesType,
  ShareVehicleStateType,
} from './shareVehicleType'

const INITIAL_SHARE_VEHICLE_CONTEXT: ShareVehicleContextType = {
  setLoading: () => null,
  setError: () => null,
  clearError: () => null,
  clearContent: () => null,
  clearConfirmPin: () => null,
  getContactsShareVehicles: () => new Promise(() => null),
  addContactShareVehicle: () => new Promise(() => null),
  deleteContactShareVehicle: () => new Promise(() => null),
  getConfirmPinToShareVehicle: () => new Promise(() => null),
  updateContactShareVehicle: () => new Promise(() => null),
  shareVehicleState: {
    loading: false,
    error: '',
    state: { contactsShareVehicles: [], confirmPinToShareVehicle: false },
  },
}

const ShareVehicleContext = createContext<ShareVehicleContextType>(
  INITIAL_SHARE_VEHICLE_CONTEXT,
)

const ShareVehicleProvider: React.FC = ({ children }) => {
  const [shareVehicleState, setShareVehicleState] = useState<
    GenericStateType<ShareVehicleStateType>
  >(INITIAL_SHARE_VEHICLE_CONTEXT.shareVehicleState)

  const setLoading = (value = true) =>
    setShareVehicleState(prevState => ({ ...prevState, loading: value }))

  const setError = (msg: string) =>
    setShareVehicleState(prevState => ({ ...prevState, loading: false, error: msg }))

  const clearError = () =>
    setShareVehicleState(prevState => ({ ...prevState, error: '' }))

  const clearContent = () => {
    setShareVehicleState(prevState => ({
      ...prevState,
      loading: false,
      error: '',
      state: {
        ...prevState.state,
        contactsShareVehicles: [],
        confirmPinToShareVehicle: false,
      },
    }))
  }

  const clearConfirmPin = () => {
    setShareVehicleState(prevState => ({
      ...prevState,
      loading: false,
      error: '',
      state: {
        ...prevState.state,
        confirmPinToShareVehicle: false,
      },
    }))
  }

  const getContactsShareVehicles = async (idVehicle: ShareVehicleGetInvitesType['idVehicle']) => {
    try {
      const data: ShareVehicleGetInvitesType = {
        idVehicle,
        takerow: 0
      }

      setLoading()

      const { data: { payload } } = await api.post<GenericResponse<ShareVehicleContactType[]>>(
        'Vehicles/GetInvites', data)

      setShareVehicleState(prevState => ({
        ...prevState,
        loading: false,
        error: '',
        state: {
          ...prevState.state,
          contactsShareVehicles: payload,
        },
      }))

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const addContactShareVehicle = async (data: ShareVehicleAddContactType) => {
    try {
      setLoading()

      const { data: { payload, message } } = await api.post<GenericResponse<ShareVehicleContactType>>(
        'Vehicles/AddVehicleByInvitation', data)

      if (message === 'No se pudo crear conductor')
        return console.log('add-gurtam-driver-error')

      setShareVehicleState(prevState => ({
        ...prevState,
        loading: false,
        error: '',
        state: {
          ...prevState.state,
          contactsShareVehicles: [...prevState.state.contactsShareVehicles, payload],
        },
      }))

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const deleteContactShareVehicle = async (id: string) => {
    try {
      setLoading()

      const { data: { payload } } = await api.get<GenericResponse<string>>(
        `Vehicles/${id}/DeleteInvitation`)

      setShareVehicleState(prevState => ({
        ...prevState,
        loading: false,
        error: '',
        state: {
          ...prevState.state,
          contactsShareVehicles: prevState.state.contactsShareVehicles.filter(({ id }) =>
            id !== payload,
          ),
        },
      }))

      return true

    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const updateContactShareVehicle = async (id: string, data: { isRestricted: boolean }) => {
    try {
      setLoading()

      const { data: { payload } } = await api.post<GenericResponse<string>>(
        `Vehicles/${id}/UpdateInvitation`, data)

      const foundIndex = shareVehicleState.state.contactsShareVehicles.findIndex(x => x.id === payload)
      const newVehicleState = shareVehicleState.state.contactsShareVehicles

      if (foundIndex > -1)
        newVehicleState[foundIndex].isRestricted = data.isRestricted

      setShareVehicleState(prevState => ({
        ...prevState,
        loading: false,
        error: '',
        state: {
          ...prevState.state,
          contactsShareVehicles: newVehicleState,
        },
      }))

      return true

    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  const getConfirmPinToShareVehicle = async (data: ShareVehicleConfirmPinType) => {
    try {
      setLoading()

      const { data: { payload } } = await api.post<GenericResponse<boolean>>('Vehicles/ValidatePin', data)

      setShareVehicleState(prevState => ({
        ...prevState,
        loading: false,
        error: '',
        state: {
          ...prevState.state,
          confirmPinToShareVehicle: payload,
        },
      }))

      return true
    } catch (err) {
      const message = getError(err)

      setError(message)

      return false
    }
  }

  return (
    <ShareVehicleContext.Provider
      value={{
        shareVehicleState,
        setLoading,
        setError,
        clearError,
        clearContent,
        clearConfirmPin,
        getContactsShareVehicles,
        addContactShareVehicle,
        deleteContactShareVehicle,
        getConfirmPinToShareVehicle,
        updateContactShareVehicle,
      }
      }>
      {children}
    </ShareVehicleContext.Provider>
  )
}

const useShareVehicle = () => useCustomContext<ShareVehicleContextType>(ShareVehicleContext)

export { ShareVehicleProvider, useShareVehicle }
